import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, Phone, Mail, FileText, Send } from "lucide-react";
import Reveal from "./Reveal";
import Logo from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, form_type: "kontakt" });
      toast.success("Zpráva odeslána. Ozveme se vám do 24 hodin.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Odeslání se nezdařilo. Zkuste to prosím znovu, nebo volejte +420 777 940 118.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="kontakt" data-testid="contact-footer" className="bg-black border-t border-gold/15">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-zinc-100 leading-tight">
              Začínáme už dnes. <span className="gold-text">Děkujeme za důvěru.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 space-y-5 text-sm">
              <div className="flex items-center gap-4">
                <span className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <User size={17} />
                </span>
                <div>
                  <p className="text-zinc-100 font-medium" data-testid="contact-name">Petr Lehnert</p>
                  <p className="text-zinc-500 text-xs">zakladatel a odpovědný zástupce</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <Phone size={17} />
                </span>
                <a href="tel:+420777940118" data-testid="contact-phone" className="text-zinc-100 hover:text-gold transition-colors">
                  +420 777 940 118
                </a>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <Mail size={17} />
                </span>
                <a href="mailto:petr.lehnert@kabumpro.com" data-testid="contact-email" className="text-zinc-100 hover:text-gold transition-colors">
                  petr.lehnert@kabumpro.com
                </a>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <FileText size={17} />
                </span>
                <p className="text-zinc-400" data-testid="contact-ico">
                  IČO: 887 833 91 · Fyzická osoba zapsaná v živnostenském rejstříku
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12">
              <Logo compact />
              <p className="mt-3 text-[11px] font-mono tracking-[0.3em] uppercase text-zinc-500 leading-relaxed" data-testid="footer-tagline">
                Jsme stavební NE-FIRMA
                <span className="block">Jsme aliance řemeslníků</span>
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-4 bg-card2 border border-white/5 rounded-2xl p-7 sm:p-9">
            <p className="text-xs font-mono tracking-[0.25em] uppercase text-gold/70">
              Napište nám
            </p>
            <input
              required
              placeholder="Jméno a příjmení"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="field-input"
              data-testid="contact-form-name"
            />
            <input
              required
              type="email"
              placeholder="E-mail"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="field-input"
              data-testid="contact-form-email"
            />
            <input
              placeholder="Telefon"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="field-input"
              data-testid="contact-form-phone"
            />
            <textarea
              required
              rows="5"
              placeholder="Vaše zpráva — popište nám stručně svůj projekt"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="field-input"
              data-testid="contact-form-message"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-full px-6 py-3.5 hover:bg-gold-light transition-colors disabled:opacity-50"
            >
              {sending ? "Odesílám…" : "Odeslat zprávu"} <Send size={15} />
            </button>
          </form>
        </Reveal>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-zinc-600">
          <p>© 2026 RESTART PRO — Petr Lehnert</p>
          <p>IČO: 887 833 91 · Fyzická osoba zapsaná v živnostenském rejstříku</p>
        </div>
      </div>
    </footer>
  );
}
